<script lang="ts" setup>
/**
 * Shared GroupBy wrapper used by Timeline and Gantt grouped layouts.
 *
 * Owns:
 * - Sidebar group labels (with expand/collapse, count, MultiSelect chip rendering, cell-typed value rendering)
 * - Expand/collapse tracking + auto-expand new groups
 * - Lazy load on first expand
 * - Reload + smart per-group refetch on `reloadViewDataHook`
 * - Pagination at the root
 * - Recursion for nested groups
 *
 * The "leaf grid" — the actual date-axis bar renderer (TimelineGrid or
 * GanttGrid) — is supplied by the caller via the default slot. The slot
 * receives `{ rows }` and is forwarded transparently through recursion so
 * nested groups pick up the same renderer.
 */
import type dayjs from 'dayjs'
import { TIMELINE_GROUP_SIDEBAR_WIDTH } from '../../../utils/timelineUtils'
import GroupBy from './DateAxisGroupBy.vue'
import type { Row as RowType } from '#imports'
import { shouldRenderCell } from '~/utils/groupbyUtils'
import type { Group } from '~/lib/types'

const props = defineProps<{
  group: Group
  loadGroups: (
    params?: any,
    group?: Group,
    options?: {
      triggerChildOnly?: boolean
    },
  ) => Promise<void>
  loadGroupData: (group: Group, force?: boolean, params?: any) => Promise<void>
  loadGroupPage: (group: Group, p: number) => Promise<void>
  groupWrapperChangePage: (page: number, groupWrapper?: Group) => Promise<void>
  depth?: number
  maxDepth?: number
}>()

const emit = defineEmits<{
  (event: 'expandRecord', row: RowType, state?: Record<string, any>): void
  (event: 'navigateTo', date: dayjs.Dayjs): void
  (event: 'update:group', value: Group): void
}>()

const vGroup = useVModel(props, 'group', emit)

const { isDark, getColor } = useTheme()

const GROUP_SIDEBAR_WIDTH = TIMELINE_GROUP_SIDEBAR_WIDTH

const _depth = props.depth ?? 0

const reloadViewDataHook = inject(ReloadViewDataHookInj, createEventHook())

const { isViewDataLoading, isPaginationLoading } = storeToRefs(useViewsStore())

// Wrapper function to manage loading state when loading group data
const _loadGroupData = async (group: Group, force?: boolean, params?: any) => {
  isViewDataLoading.value = true
  isPaginationLoading.value = true

  await props.loadGroupData(group, force, params)

  isViewDataLoading.value = false
  isPaginationLoading.value = false
}

// Wrapper function to manage loading state when loading groups
const _loadGroups = async (params?: any, group?: Group, options?: { triggerChildOnly?: boolean }) => {
  isViewDataLoading.value = true
  isPaginationLoading.value = true

  await props.loadGroups(params, group, options)

  isViewDataLoading.value = false
  isPaginationLoading.value = false
}

// Ref the scroll area so the overlay slot can use it as a measurement
// origin for absolute-positioned cross-group overlays.
const scrollAreaRef = ref<HTMLElement | null>(null)

// Expanded groups tracker — plain object for reliable Vue reactivity
const expandedGroups = ref<Record<string, boolean>>({})
// Track keys we've already processed so we don't re-expand manually collapsed groups
const seenKeys = new Set<string>()

const isExpanded = (key: string) => !!expandedGroups.value[key]

const toggleGroup = async (grp: any) => {
  const key = String(grp.key)
  if (expandedGroups.value[key]) {
    expandedGroups.value = { ...expandedGroups.value, [key]: false }
  } else {
    expandedGroups.value = { ...expandedGroups.value, [key]: true }
    // Load data on expand if not yet loaded
    if (grp.nested) {
      if (!grp.children?.[0]?.children?.length) {
        await _loadGroups({}, grp, { triggerChildOnly: true })
      }
    } else {
      if (!grp.rows?.length) {
        await _loadGroupData(grp)
      }
    }
  }
}

// Auto-expand all groups and load data when children become available
watch(
  () => vGroup.value?.children,
  (children) => {
    if (!children) return

    // If this is the root group, check if we have a completely new set of children
    // (different grouping configuration). If so, reset tracking state.
    if (vGroup.value.root && children.length > 0) {
      const currentKeys = new Set(children.map((g) => String(g.key)))
      const hasNewKeys = Array.from(currentKeys).some((k) => !seenKeys.has(k))
      const hasOldKeys = Array.from(seenKeys).some((k) => !currentKeys.has(k))

      // If we have both new keys and missing old keys, this is a different grouping
      if (hasNewKeys && hasOldKeys) {
        seenKeys.clear()
        expandedGroups.value = {}
      }
    }

    let changed = false
    const updates: Record<string, boolean> = {}
    for (const grp of children) {
      const key = String(grp.key)
      // Only auto-expand groups we haven't seen before
      if (!seenKeys.has(key)) {
        seenKeys.add(key)
        updates[key] = true
        changed = true
        // Load data for leaf groups
        if (!grp.nested && !grp.rows?.length) {
          _loadGroupData(grp)
        }
        // Load sub-groups for nested groups
        if (grp.nested && !grp.children?.length) {
          _loadGroups({}, grp, { triggerChildOnly: true })
        }
      }
    }
    if (changed) {
      expandedGroups.value = { ...expandedGroups.value, ...updates }
    }
  },
  { immediate: true },
)

const reloadViewDataHandler = async () => {
  if (vGroup.value.nested) {
    // Snapshot counts before reload so we can detect which groups changed
    const oldCounts = new Map<string, number>()
    for (const child of vGroup.value.children ?? []) {
      oldCounts.set(String(child.key), child.count)
    }

    await _loadGroups({}, vGroup.value)

    // Only reload row data for expanded leaf groups whose count changed
    // (the group a record left and the group it moved to)
    const leafReloads = (vGroup.value.children ?? [])
      .filter((child) => {
        if (child.nested || !isExpanded(String(child.key))) return false
        const oldCount = oldCounts.get(String(child.key))
        return oldCount === undefined || oldCount !== child.count
      })
      .map((child) => _loadGroupData(child, true))
    await Promise.all(leafReloads)
  } else {
    await _loadGroupData(vGroup.value, true)
  }
}

onMounted(async () => {
  reloadViewDataHook?.on(reloadViewDataHandler)

  // Root auto-load: if root has no children, load groups
  if (vGroup.value.root === true && !vGroup.value?.children?.length) {
    await _loadGroups({}, vGroup.value)
  }
})

onBeforeUnmount(async () => {
  reloadViewDataHook?.off(reloadViewDataHandler)
})
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Scrollable groups area -->
    <div ref="scrollAreaRef" class="flex-1 min-h-0 overflow-y-auto relative">
      <!-- Optional overlay slot — rendered as an absolute child of the
           scroll area so consumers (e.g. Gantt cross-group arrow layer)
           can draw across multiple groups inside the same scroll context.
           Passed expandedGroups + scroll container ref + group tree so
           callers can compute global y positions. -->
      <slot name="overlay" :expanded-groups="expandedGroups" :scroll-area-el="scrollAreaRef" :group="vGroup" />
      <!-- CSS Grid layout: left sidebar (group labels) + right date-axis area -->
      <div class="nc-date-axis-group-grid" :style="{ display: 'grid', gridTemplateColumns: `${GROUP_SIDEBAR_WIDTH}px 1fr` }">
        <template v-for="grp of vGroup?.children ?? []" :key="grp.key">
          <!-- Left cell: group label — min-h matches right cell when collapsed -->
          <div
            class="nc-date-axis-group-label border-b border-r border-nc-border-gray-medium px-3 py-2 bg-nc-bg-default cursor-pointer select-none hover:bg-nc-bg-gray-extralight transition-colors overflow-hidden"
            @click="toggleGroup(grp)"
          >
            <div class="flex items-center gap-1.5 w-full">
              <GeneralIcon
                icon="chevronDown"
                class="flex-shrink-0 text-nc-content-gray-muted transform transition-transform duration-200"
                :class="{ '-rotate-90': !isExpanded(String(grp.key)) }"
              />

              <div class="flex items-center min-w-0 flex-1 gap-2 overflow-hidden">
                <!-- Group value rendering -->
                <template v-if="grp.column?.uidt === 'MultiSelect'">
                  <div class="flex flex-wrap gap-1 min-w-0">
                    <a-tag
                      v-for="[tagIndex, tag] of Object.entries(grp.key.split(','))"
                      :key="`tag-${grp.column.id}-${tag}`"
                      class="!py-0 !px-[10px] !rounded-full !m-0"
                      :color="
                        getSelectTypeFieldOptionBgColor({
                          isDark,
                          color: grp.color?.split(',')[+tagIndex] || '#ccc',
                          getColor,
                          isColorCodeEnabled: parseProp(grp.column?.meta)?.isColorCodeEnabled !== false,
                        })
                      "
                    >
                      <span
                        :style="{
                          color: getSelectTypeFieldOptionTextColor({
                            isDark,
                            color: grp.color?.split(',')[+tagIndex] || '#ccc',
                            getColor,
                            isColorCodeEnabled: parseProp(grp.column?.meta)?.isColorCodeEnabled !== false,
                          }),
                          fontSize: '12px',
                          fontWeight: 500,
                        }"
                      >
                        {{ tag in GROUP_BY_VARS.VAR_TITLES ? GROUP_BY_VARS.VAR_TITLES[tag] : tag }}
                      </span>
                    </a-tag>
                  </div>
                </template>

                <div
                  v-else-if="!(grp.key in GROUP_BY_VARS.VAR_TITLES) && shouldRenderCell(grp.column)"
                  class="flex min-w-0 truncate"
                >
                  <template v-for="(val, ind) of parseKey(grp)" :key="ind">
                    <SmartsheetGridGroupByLabel v-if="val" :column="grp.column" :model-value="val" />
                    <span v-else class="text-nc-content-gray-muted text-sm">{{ $t('labels.noMappedValue') }}</span>
                  </template>
                </div>

                <a-tag
                  v-else
                  class="!py-0 !px-[10px] !m-0 !max-w-[calc(100%-2rem)] truncate"
                  :class="grp.column?.uidt === 'SingleSelect' ? '!rounded-full' : '!rounded-md'"
                  :color="
                    getSelectTypeFieldOptionBgColor({
                      isDark,
                      color: grp.color || '#ccc',
                      getColor,
                      isColorCodeEnabled: parseProp(grp.column?.meta)?.isColorCodeEnabled !== false,
                    })
                  "
                >
                  <span
                    class="font-semibold text-[12px]"
                    :style="{
                      color: getSelectTypeFieldOptionTextColor({
                        isDark,
                        color: grp.color || '#ccc',
                        getColor,
                        isColorCodeEnabled: parseProp(grp.column?.meta)?.isColorCodeEnabled !== false,
                      }),
                    }"
                  >
                    <template v-if="grp.key in GROUP_BY_VARS.VAR_TITLES">{{ GROUP_BY_VARS.VAR_TITLES[grp.key] }}</template>
                    <template v-else>{{ parseKey(grp)?.join(', ') }}</template>
                  </span>
                </a-tag>

                <!-- Record count — right-aligned -->
                <span class="text-[11px] text-nc-content-gray-muted ml-auto flex-shrink-0">
                  {{ grp.count }}
                </span>
              </div>
            </div>
          </div>

          <!-- Right cell: date-axis content — with expand/collapse transition -->
          <div class="border-b border-nc-border-gray-medium overflow-hidden">
            <div v-if="isExpanded(String(grp.key))">
              <!-- Leaf group: render the caller's grid via the default slot.
                   Slot receives the leaf's row array; caller decides which grid
                   component to instantiate (TimelineGrid / GanttGrid). -->
              <slot v-if="!grp.nested && grp.rows" :rows="grp.rows" />

              <!-- Nested group: recurse. Forward the default slot transparently
                   so nested levels render the same caller-supplied grid. -->
              <GroupBy
                v-else-if="grp.nested"
                :group="grp"
                :load-groups="loadGroups"
                :load-group-data="loadGroupData"
                :load-group-page="loadGroupPage"
                :group-wrapper-change-page="groupWrapperChangePage"
                :depth="_depth + 1"
                :max-depth="maxDepth"
                @expand-record="(row: RowType, state?: Record<string, any>) => emit('expandRecord', row, state)"
                @navigate-to="(date: dayjs.Dayjs) => emit('navigateTo', date)"
              >
                <template #default="bindings">
                  <slot v-bind="bindings" />
                </template>
              </GroupBy>

              <!-- Loading state -->
              <div v-else class="flex items-center justify-center py-4 text-nc-content-gray-muted">
                <GeneralLoader size="medium" />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Pagination for root group (sticky at bottom) -->
    <LazySmartsheetPagination
      v-if="vGroup.root && vGroup.paginationData"
      v-model:pagination-data="vGroup.paginationData"
      align-count-on-right
      :custom-label="$t('objects.groups')"
      align-left
      show-api-timing
      :change-page="(p: number) => groupWrapperChangePage(p, vGroup)"
      :hide-sidebars="true"
    />
  </div>
</template>

<style scoped lang="scss">
.nc-date-axis-group-label {
  align-self: stretch;
}
</style>
